import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { SessionProvider } from "./contexts/SessionContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { LoginForm } from "./components/LoginForm";
import { SignUpForm } from "./components/SignUpForm";
import { ForgotPasswordForm } from "./components/ForgotPasswordForm";
import { ResetPasswordForm } from "./components/ResetPasswordForm";
import { HotelExtranet } from "./components/extranets/HotelExtranet";
import { SaasExtranet } from "./components/extranets/SaasExtranet";
import { InternalExtranet } from "./components/extranets/InternalExtranet";
import { HomePage } from "./components/HomePage";
import { Settings } from "./components/Settings";
import { HotelOnboarding } from "./components/onboarding/HotelOnboarding";
import { SaasOnboarding } from "./components/onboarding/SaasOnboarding";
import { SaasHotelOnboardingWidget } from "./components/onboarding/SaasHotelOnboardingWidget";
import { lazy, Suspense } from "react";
import { GoogleAnalytics } from "./components/GoogleAnalytics";
import { ROUTES } from "./lib/routes";

// Lazy load Scalar API Documentation to reduce initial bundle size
const ScalarApiDocumentation = lazy(() => import("./components/ScalarApiDocumentation").then(module => ({ default: module.ScalarApiDocumentation })));

function App() {
  console.log('App component is rendering...');
  
  return (
    <BrowserRouter>
      <GoogleAnalytics />
      <SessionProvider>
        <NotificationProvider>
          <Routes>
            {/* Public routes - Login and Signup will ignore theme */}
            <Route path={ROUTES.login} element={<LoginForm />} />
            <Route path={ROUTES.signup} element={<SignUpForm />} />
            <Route path={ROUTES.forgotPassword} element={<ForgotPasswordForm />} />
            <Route path={ROUTES.resetPassword} element={<ResetPasswordForm />} />
            <Route path={ROUTES.hotelOnboarding} element={<HotelOnboarding />} />
            <Route path={ROUTES.saasOnboarding} element={<SaasOnboarding />} />
            <Route path="/onboarding/widget/:saasId" element={<SaasHotelOnboardingWidget />} />

            {/* Other routes */}
            <Route path={ROUTES.home} element={<HomePage />} />
            <Route 
              path={ROUTES.apiDocumentation} 
              element={
                <Suspense fallback={
                  <div className="min-h-screen bg-white flex items-center justify-center">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#8B5CF6] mx-auto mb-4"></div>
                      <p className="text-gray-600">Loading API documentation...</p>
                    </div>
                  </div>
                }>
                  <ScalarApiDocumentation />
                </Suspense>
              } 
            />

            {/* Protected routes */}
            <Route
              path="/extranet/hotel/:section?/:sub?"
              element={
                <ProtectedRoute requiredUserType="hotel">
                  <HotelExtranet />
                </ProtectedRoute>
              }
            />
            <Route
              path="/extranet/saas/:section?/:sub?"
              element={
                <ProtectedRoute requiredUserType="saas">
                  <SaasExtranet />
                </ProtectedRoute>
              }
            />
            <Route
              path="/extranet/internal/:section?/:sub?"
              element={
                <ProtectedRoute requiredUserType="internal">
                  <InternalExtranet />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.settings}
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />

            {/* Anything else falls back to the landing page. */}
            <Route path="*" element={<Navigate to={ROUTES.home} replace />} />
          </Routes>
        </NotificationProvider>
      </SessionProvider>
    </BrowserRouter>
  );
}

export default App;
